import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { describeSourceSelection, type SourceMultiSelectOption } from './sourceSelection';

// Checkbox multi-select for one Settings › Data sources row (FIG-409 · UC-233).
// Same behaviour as the Reports tab control in production before FIG-345
// (git show 2ebdd8b8^:src/components/organisation/OrganisationReportsTab.tsx)
// and the live Inventory tab control: one checkbox per source, "Select all",
// and the last ticked source can't be unticked (AC2). The parent keeps the
// draft and saves it with its own Save / Cancel buttons.

interface SourceMultiSelectProps {
  id: string;
  /** Accessible name of the trigger, e.g. "Variants data source". */
  label: string;
  options: SourceMultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Trigger text to show instead of the computed one (e.g. the saved label before any change). */
  summary?: string;
  disabled?: boolean;
}

export const SourceMultiSelect: React.FC<SourceMultiSelectProps> = ({ id, label, options, selected, onChange, summary, disabled }) => {
  const ticked = options.filter((o) => selected.includes(o.value)).map((o) => o.value);
  const allTicked = ticked.length === options.length;
  // Also offered while the draft is untouched (summary given) even when every box
  // is ticked, so an explicit selection or an unset row can be saved as the
  // dynamic "All" on purpose. If the row is already "All", nothing changes.
  const showSelectAll = !allTicked || summary !== undefined;

  const toggle = (value: string) => {
    if (ticked.includes(value)) {
      if (ticked.length === 1) return; // never empty (AC2)
      onChange(ticked.filter((v) => v !== value));
    } else {
      // Keep the list order, whatever order the boxes were ticked in.
      onChange(options.map((o) => o.value).filter((v) => v === value || ticked.includes(v)));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-label={label}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{summary ?? describeSourceSelection(ticked, options)}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-2" align="start">
        <div className="mb-1 flex items-center justify-between px-2 py-1.5">
          <span className="text-sm font-medium">Data sources</span>
          {showSelectAll && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-primary hover:text-primary"
              onClick={() => onChange(options.map((o) => o.value))}
            >
              Select all
            </Button>
          )}
        </div>
        <div className="space-y-1" role="group" aria-label={label}>
          {options.map((option) => {
            const checked = ticked.includes(option.value);
            const isOnlyTicked = checked && ticked.length === 1;
            const checkboxId = `${id}-${option.value}`;
            return (
              <div key={option.value} className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent">
                <Checkbox
                  id={checkboxId}
                  checked={checked}
                  disabled={isOnlyTicked}
                  onCheckedChange={() => toggle(option.value)}
                />
                <label
                  htmlFor={checkboxId}
                  className={cn('flex-1 text-sm', isOnlyTicked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                >
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SourceMultiSelect;
